import clsx from 'clsx';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { TicketModel, ParlayTicketModel, AcceptTicketStatus } from '@/hooks/query';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useTranslations } from 'next-intl';
import { ConfirmBetTicketStatus } from '../confirm-bet-ticket-status';
import { confirmBetStyle } from '../../confirm-bet.style';
import { ConfirmBetSimpleChoice } from '../confirm-bet-simple-choice';
import { useFloatBetslip } from '@/features/betslip/hook/use-float-betslip';
import { useAsideBetslip } from '@/features/betslip/hook/use-aside-betslip';
import { TicketOddsBoostIcon, TicketShieldIcon } from '@/components/icons/svg-icons';
import { matchSome } from '@nf/utils-common/fpw/monadic';
import { OddsBoostPromoIcon } from '@/features/betslip/voucher/components/voucher-dropdown/odds-boost-promo-voucher';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import Image from 'next/image';
import { goldenDuelStorageService } from '@/services/golden-duel/golden-duel-storage.service';

interface ConfirmBetTicketHeaderProps {
	isParlay: boolean;
	tickets: TicketModel[];
	parlayTickets?: ParlayTicketModel[];
	status: AcceptTicketStatus;
}

export const ConfirmBetTicketHeader = ({
	isParlay,
	tickets,
	parlayTickets,
	status
}: ConfirmBetTicketHeaderProps) => {
	const { currencyName, currencySymbol } = useBetslipContext();
	const { SiteConfigsData } = useGetSiteConfigs();
	const t = useTranslations('General');
	const { activeFloatBetSlip } = useFloatBetslip()
	const { activeAsideBetSlip } = useAsideBetslip();
	const isFloatStyle = activeFloatBetSlip || activeAsideBetSlip;

	const singleTicket = tickets[0] ?? {};
	const voucherIcon = matchSome(singleTicket)
		.on(
			(ticket: TicketModel) => ticket.RewardModel?.UseVoucher === 'pro_ob',
			() => <OddsBoostPromoIcon />
		)
		.on(
			(ticket: TicketModel) => ticket.OddsBoost && ticket.OddsBeforeOddsBoost,
			() => <TicketOddsBoostIcon />
		)
		.on(
			(ticket: TicketModel) => ['rfb', 'rfb_behavior'].includes(ticket.RewardModel?.UseVoucher ?? ''),
			() => <TicketShieldIcon />
		).otherwise(() => null);
	const transIds = goldenDuelStorageService.getTransactionIds();

	return (
		<div className={clsx(
			'confirm-bet-header',
			confirmBetStyle.ticktetheader,
			{ [confirmBetStyle.floatTicketHeader]: isFloatStyle },
			{
				[confirmBetStyle.headerRunning]: status === 'running',
				[confirmBetStyle.headerReject]: status === 'reject',
			}
		)}
		>
			<div className="confirm-bet-header__status">
				<ConfirmBetTicketStatus status={status} isHeader={true} />
			</div>
			<div className="confirm-bet-header__info">
				<div className="confirm-bet-header__title">
					{
						isParlay ?
							parlayTickets?.map(parlayTicket => {
								return `${parlayTicket?.BetCount}x ${SiteConfigsData?.EnableCurrencyDisplay ? parlayTicket?.CustomCurrencyName ?? currencyName : ''} ${currencySymbol}${addCommasWithFix(parlayTicket?.stake || 0)} ${parlayTicket?.Name}`;
							}).join(', ') :
							`${SiteConfigsData?.EnableCurrencyDisplay ? tickets[0].CustomCurrencyName ?? currencyName : ''} ${currencySymbol}${addCommasWithFix(tickets[0]?.stake || 0)} ${t('lbl_SingleBet')}`
					}

					{voucherIcon}
				</div>

				{/* This section only visible in simplified mode */}
				<div className="confirm-bet-header__content">
					<ConfirmBetSimpleChoice tickets={tickets} />
				</div>
			</div>
			{
				status === 'running' && transIds.length > 0 && transIds.includes(singleTicket.TransId ?? '') && (
					<Image
						src={'/images/promotion/golden-duel/cf-nba.svg'}
						alt={'Active Ticket'}
						width={30}
						height={20}
					/>
				)
			}
		</div>
	);
}
